import sanitize from 'sanitize-filename';

const FALLBACK_BASENAME = 'file';

/**
 * Sanitize a string for use as a filename. Safe for Windows, macOS, and Unix.
 *
 * Removes or replaces:
 * - Control characters (e.g. null, newline)
 * - Reserved characters: / \ : * ? " < > |
 * - Leading/trailing spaces and periods (Windows)
 * - Reserved names (CON, PRN, NUL, COM1-9, LPT1-9)
 * - Path segments (e.g. "foo/../bar" → "foobar")
 *
 * Use this for any user-provided filename before building storage paths or
 * sending to the backend to avoid issues on some devices (e.g. whitespace,
 * special characters).
 *
 * @param filename - Raw filename (e.g. from File.name)
 * @param options.replacement - Replace invalid chars with this (default: remove them)
 * @returns Sanitized filename, or a safe fallback if result would be empty
 */
export function sanitizeFilename(
  filename: string,
  options?: { replacement?: string }
): string {
  if (!filename || typeof filename !== 'string') {
    return FALLBACK_BASENAME;
  }
  const result = sanitize(filename.trim(), {
    replacement: options?.replacement ?? '',
  });
  if (result === '') {
    // Preserve extension if present so we don't break type detection
    const lastDot = filename.lastIndexOf('.');
    const ext = lastDot > 0 ? filename.slice(lastDot) : '';
    return `${FALLBACK_BASENAME}${ext}`;
  }
  return result;
}

/**
 * Remove emoji and other extended pictographic characters so the filename
 * is safe for object storage keys (e.g. Supabase Storage rejects emojis in keys).
 */
function stripEmoji(str: string, replacement: string = ''): string {
  return str.replace(/\p{Extended_Pictographic}/gu, replacement);
}

/**
 * Sanitize a filename for upload/storage: invalid characters removed,
 * spaces replaced with underscore, and emojis stripped (many storage backends
 * reject emojis in object keys, e.g. Supabase Storage).
 */
export function sanitizeFilenameForUpload(filename: string): string {
  const withoutEmoji = stripEmoji(filename, '');
  const base = sanitizeFilename(withoutEmoji, { replacement: '_' });
  return base.replace(/\s+/g, '_');
}

/**
 * Turn a raw filename into a human display name. Strips the extension,
 * treats `_`/`-` and camelCase boundaries as word breaks, and Title Cases
 * the result: `rusty_old_barrel.glb` -> `Rusty Old Barrel`, `treasure_chest_v2`
 * -> `Treasure Chest V2`. Falls back to `Untitled` when nothing remains.
 */
export function humanizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') return 'Untitled';
  const words = filename
    .replace(/\.[^/.]+$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return 'Untitled';
  return words.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Lowercased, hyphenated, extension-stripped slug for use as a URL/filename
 * stem (≤ 40 chars). Falls back to 'asset' when the input has nothing slug-able.
 */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'asset';
}
